import * as React from 'react';
import {connect} from 'react-redux';

import {Field, formValueSelector, reset, change} from 'redux-form';

import {Label} from 'shells/form/label';
import {nonNull} from 'shells/form/validation/non-null';
import {ReduxFormSelectTwoLineIcon} from 'shells/redux-form-fields/select-two-line-icon/select-two-line-icon';
import type {ListOptionWithIcon} from 'shells/select-option-components/option-with-icon';

import type {NotifiableAliasType} from 'nutshell-graphql-types';

import {LeadWonForm, AddTagForm, AudienceForm, LeadCreatedForm} from '../../../core/form/fields';

import type {GoalTypeEnum, GoalTypeValue} from './constants';
import {FORM_NAME} from './drip-sequence-sidebar-form';
import {LinkClickedForm} from './sidebar-forms/link-clicked-form';
import {NotifyUsersGoalMet} from './sidebar-forms/notify-users-goal-met';
import {StopOnGoalMet} from './sidebar-forms/stop-on-goal-met';

import styles from './forms.module.css';

const FORMS = {
    LEAD_WON: LeadWonForm,
    ADD_TAG: AddTagForm,
    LEAD_CREATED: LeadCreatedForm,
    LINK_CLICKED: LinkClickedForm,
    ADD_TO_AUDIENCE: AudienceForm,
} as const;

const goalSelector = formValueSelector(FORM_NAME);

// @ts-expect-error
function mapStateToProps(state): StateProps {
    return {
        goalType: goalSelector(state, 'goalType'),
    };
}

// @ts-expect-error
function mapDispatchToProps(dispatch): DispatchProps {
    return {
        onGoalTypeChange: (newValue?: GoalTypeEnum | null) => {
            dispatch(reset(FORM_NAME));
            dispatch(change(FORM_NAME, 'goalType', newValue));
        },
    };
}

type OwnProps = {
    isNotifyUsersOnGoalMet: boolean;
    setIsNotifyUsersOnGoalMet: (arg1: boolean) => void;
    onSelectUsers: (arg1: ListOptionWithIcon[]) => void;
    onClearSelectedUsers: () => void;
    notifiableUserIds: string[];
    notifiableAssignees: NotifiableAliasType[];
    isStopOnGoalMet: boolean;
    setIsStopOnGoalMet: (arg1: boolean) => void;
    dripSequenceId: string;
    goalTypeValues: GoalTypeValue[];
};

type StateProps = {
    goalType?: GoalTypeEnum;
};

type DispatchProps = {
    onGoalTypeChange: (newValue?: GoalTypeEnum | null | undefined) => void;
};

type Props = OwnProps & StateProps & DispatchProps;

export function AddGoalFormComponent(props: Props) {
    const Component = props.goalType ? FORMS[props.goalType] : undefined;

    const {goalType, onGoalTypeChange} = props;

    React.useEffect(() => {
        onGoalTypeChange(goalType);
    }, [goalType, onGoalTypeChange]);

    return (
        <div className={styles['wrapping-container']}>
            <div className={styles['form-description']}>
                Set up a goal for your drip sequence, which can optionally trigger an automated next
                step.
            </div>
            <Label htmlFor='triggerType'>What is the goal of this drip sequence?</Label>
            <Field
                name='goalType'
                component={ReduxFormSelectTwoLineIcon}
                options={props.goalTypeValues}
                validate={[nonNull]}
                placeholder='Select a goal'
            />
            {Component ? (
                <>
                    <Component dripSequenceId={props.dripSequenceId} tagType='CONTACT' />
                    {NutClientConfig.NUT14666ShowNotifyUsersGoalMet ? (
                        <NotifyUsersGoalMet
                            isNotifyUsersOnGoalMet={props.isNotifyUsersOnGoalMet}
                            setIsNotifyUsersOnGoalMet={props.setIsNotifyUsersOnGoalMet}
                            onSelectUsers={props.onSelectUsers}
                            onClearSelectedUsers={props.onClearSelectedUsers}
                            notifiableUserIds={props.notifiableUserIds}
                            notifiableAssignees={props.notifiableAssignees}
                        />
                    ) : undefined}
                    <StopOnGoalMet
                        isStopOnGoalMet={props.isStopOnGoalMet}
                        setIsStopOnGoalMet={props.setIsStopOnGoalMet}
                    />
                </>
            ) : undefined}
        </div>
    );
}

// @ts-expect-error
const connector = connect<Props, OwnProps, _, _, _, _>(mapStateToProps, mapDispatchToProps);

export const AddGoalForm = connector(AddGoalFormComponent);
